import { useCallback, useEffect, useState } from 'react';
import { Tag } from '../../api/Tag';
import apiService from '../../api/apiService';
import {
  addOrSelectTag,
  buildNextSelectedTagIds,
  deleteManagedTag,
  findTagSuggestions,
} from '../../domains/tags';

interface UseTagManagerOptions {
  availableTags: Tag[];
  selectedTagIds: number[];
  setAvailableTags: React.Dispatch<React.SetStateAction<Tag[]>>;
  setSelectedTagIds: React.Dispatch<React.SetStateAction<number[]>>;
  onMessage: (message: string, isError: boolean) => void;
  token: string | null;
}

interface UseTagManagerResult {
  newTagName: string;
  setNewTagName: React.Dispatch<React.SetStateAction<string>>;
  tagSuggestions: Tag[];
  handleAddOrSelectTag: () => Promise<void>;
  handleDeleteTag: (tag: Tag) => Promise<void>;
  handleSelectSuggestion: (tag: Tag) => void;
  toggleTagSelection: (tag: Tag, isSelected: boolean) => void;
}

const clearTagComposer = (
  setNewTagName: React.Dispatch<React.SetStateAction<string>>,
  setTagSuggestions: React.Dispatch<React.SetStateAction<Tag[]>>
) => {
  setNewTagName('');
  setTagSuggestions([]);
};

export const useTagManager = ({
  availableTags,
  selectedTagIds,
  setAvailableTags,
  setSelectedTagIds,
  onMessage,
  token,
}: UseTagManagerOptions): UseTagManagerResult => {
  const [newTagName, setNewTagName] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState<Tag[]>([]);

  useEffect(() => {
    setTagSuggestions(findTagSuggestions(availableTags, newTagName));
  }, [availableTags, newTagName]);

  const handleAddOrSelectTag = useCallback(async () => {
    const result = await addOrSelectTag({
      newTagName,
      token,
      availableTags,
      selectedTagIds,
      createTag: apiService.addTag,
    });

    if (result.action === 'selected-existing' && result.selectedTagId !== undefined) {
      setSelectedTagIds((previous) => [...previous, result.selectedTagId!]);
    }

    if (result.action === 'created' && result.createdTag && result.selectedTagId !== undefined) {
      setAvailableTags((previous) => [...previous, result.createdTag!]);
      setSelectedTagIds((previous) => [...previous, result.selectedTagId!]);
    }

    if (result.message) {
      onMessage(result.message, Boolean(result.isError));
    }

    clearTagComposer(setNewTagName, setTagSuggestions);
  }, [
    availableTags,
    newTagName,
    onMessage,
    selectedTagIds,
    setAvailableTags,
    setSelectedTagIds,
    token,
  ]);

  const handleDeleteTag = useCallback(async (tag: Tag) => {
    const result = await deleteManagedTag({
      tag,
      token,
      confirmDelete: () => window.confirm(`Voulez-vous vraiment supprimer le tag "${tag.name}" ?`),
      deleteTag: apiService.deleteTag,
    });

    if (result.shouldAbort) {
      if (result.message) {
        onMessage(result.message, Boolean(result.isError));
      }
      return;
    }

    if (result.deletedTagId !== undefined) {
      setAvailableTags((previous) =>
        previous.filter((currentTag) => currentTag.id !== result.deletedTagId)
      );
      setSelectedTagIds((previous) => previous.filter((id) => id !== result.deletedTagId));
    }

    if (result.message) {
      onMessage(result.message, Boolean(result.isError));
    }
  }, [onMessage, setAvailableTags, setSelectedTagIds, token]);

  const handleSelectSuggestion = useCallback((tag: Tag) => {
    if (!selectedTagIds.includes(tag.id)) {
      setSelectedTagIds((previous) => [...previous, tag.id]);
    }

    clearTagComposer(setNewTagName, setTagSuggestions);
    onMessage(`Tag "${tag.name}" sélectionné via suggestion.`, false);
  }, [onMessage, selectedTagIds, setSelectedTagIds]);

  const toggleTagSelection = useCallback((tag: Tag, isSelected: boolean) => {
    setSelectedTagIds((previous) =>
      buildNextSelectedTagIds(previous, tag.id, isSelected)
    );
  }, [setSelectedTagIds]);

  return {
    newTagName,
    setNewTagName,
    tagSuggestions,
    handleAddOrSelectTag,
    handleDeleteTag,
    handleSelectSuggestion,
    toggleTagSelection,
  };
};

export default useTagManager;
